/* eslint-disable react/prop-types */
// /components/TextEffects/TextEffectColorCycle.js
import React, { useEffect, useRef, useState } from 'react'
import './text-effects.css'

export const TextEffectColorCycle = ({
	children,
	type = 'all', // 'all' | 'in-place'
	colors = ['#ff0000', '#00ff00', '#0000ff'],
	duration = 300,
	limited = false,
	trigger = 'viewport', // 'viewport' | 'hover' | 'instant'
	hoverTarget = 'parent',
	delay = 0,
	direction = 'forward', // 'forward' | 'reverse' | 'pingpong'
	style = {},
	className = ''
}) => {
	const containerRef = useRef(null)
	const [active, setActive] = useState(trigger === 'instant')
	const [triggerCount, setTriggerCount] = useState(0)
	const [coloredLetters, setColoredLetters] = useState([])

	const mod = (a, n) => (n === 0 ? 0 : ((a % n) + n) % n)

	const extractText = (nodes) => {
		let text = ''
		React.Children.forEach(nodes, (child) => {
			if (typeof child === 'string') text += child
			else if (child?.props?.children)
				text += extractText(child.props.children)
		})
		return text
	}
	const fullText = extractText(children)
	const letters = fullText.split('')

	useEffect(() => {
		if (trigger !== 'viewport' || !containerRef.current) return
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					setTimeout(() => {
						setTriggerCount((p) => p + 1)
						setActive(true)
					}, delay)
				}
			},
			{ threshold: 0.2 }
		)
		obs.observe(containerRef.current)
		return () => obs.disconnect()
	}, [trigger, delay])

	useEffect(() => {
		if (trigger !== 'hover' || !containerRef.current) return
		const el =
			hoverTarget === 'parent'
				? containerRef.current.parentElement
				: containerRef.current
		if (!el) return
		const onEnter = () => {
			setTriggerCount((p) => p + 1)
			setActive(true)
		}
		el.addEventListener('mouseenter', onEnter)
		return () => el.removeEventListener('mouseenter', onEnter)
	}, [trigger, hoverTarget])

	useEffect(() => {
		if (!active || !letters.length || !Array.isArray(colors)) return

		let step = 0
		let intervalId = null
		const isAll = type === 'all'

		const palette = colors.filter((c) => c !== null)
		const paletteLen = Math.max(palette.length, 1)

		const activeIndices = letters
			.map((_, i) => i)
			.filter((i) => {
				const c = colors[i]
				return limited ? i < colors.length && c !== null : c !== null
			})

		// Map direction so that:
		// - 'forward' visually moves left→right across the text
		// - 'reverse' visually moves right→left
		const getEffectiveStepAll = (len) => {
			if (len <= 1) return 0
			if (direction === 'forward') return -step // ⬅ key swap
			if (direction === 'reverse') return step // ⬅ key swap
			// pingpong over palette length (symmetric visually)
			const period = 2 * (len - 1)
			const t = mod(step, period)
			return t < len ? t : period - t
		}

		const getEffectiveStepLocal = (len) => {
			if (len <= 1) return 0
			if (direction === 'forward') return -step // ⬅ key swap
			if (direction === 'reverse') return step // ⬅ key swap
			const period = 2 * (len - 1)
			const t = mod(step, period)
			return t < len ? t : period - t
		}

		const updateColors = () => {
			if (!activeIndices.length || !paletteLen) {
				setColoredLetters(
					letters.map((ch) => ({ char: ch, color: null }))
				)
				return
			}

			const effAll = getEffectiveStepAll(paletteLen)

			const newColored = letters.map((char, i) => {
				if (colors[i] === null) return { char, color: null }

				if (isAll) {
					const pos = activeIndices.indexOf(i)
					if (pos === -1) return { char, color: null }
					const colorIdx = mod(pos + effAll, paletteLen)
					const color = palette[colorIdx]
					return { char, color }
				}

				const def = colors[i]
				if (Array.isArray(def) && def.length) {
					const effLocal = getEffectiveStepLocal(def.length)
					const color = def[mod(effLocal, def.length)]
					return { char, color: color ?? null }
				}

				const pos = activeIndices.indexOf(i)
				if (pos === -1) return { char, color: null }
				const colorIdx = mod(pos + effAll, paletteLen)
				const color = palette[colorIdx]
				return { char, color }
			})

			setColoredLetters(newColored)
			step++
		}

		updateColors()
		intervalId = setInterval(updateColors, duration)
		return () => clearInterval(intervalId)
	}, [
		triggerCount,
		active,
		type,
		colors,
		duration,
		limited,
		letters.length,
		direction
	])

	const renderWithColor = (nodes, indexRef = { i: 0 }) =>
		React.Children.map(nodes, (child) => {
			if (typeof child === 'string') {
				return child.split('').map((char) => {
					const data = coloredLetters[indexRef.i]
					indexRef.i++
					return (
						<span
							key={indexRef.i}
							style={{
								color: data?.color || 'inherit',
								transition: `color ${duration / 2}ms linear`
							}}
						>
							{char}
						</span>
					)
				})
			}
			if (child?.props?.children) {
				return React.cloneElement(child, {
					children: renderWithColor(child.props.children, indexRef)
				})
			}
			return child
		})

	return (
		<span
			ref={containerRef}
			className={`we-text-colorcycle ${className}`}
			style={{ display: 'inline-block', ...style }}
		>
			{coloredLetters.length ? renderWithColor(children) : children}
		</span>
	)
}
